import {Injectable} from "@angular/core";
import {BleCore, CommandDefinitions} from "./BleCore";
import {SpinalLocation, ThermalProgramStep} from "../model/weak-entities";
import {BLE} from "@awesome-cordova-plugins/ble/ngx";
import {
  ThermalConfig,
  thermalData,
  ThermalDataReading,
  ThermalReadingData,
  ThermalScanStates,
  ThermalStatus
} from "../model/thermal";
import {ThermalScanMethod, ThermalSettings} from "../model/scanSettings";
import {Events} from "ionic-angular";
import {ClaEvents} from "../model/events";
import {delay} from "rxjs/operators";
import {LogService} from "../providers/logging/log.service";

@Injectable()
export class BleThermal extends BleCore {
  deviceId: string = '844BEBE1-D2AA-898C-1F13-DC826C0D206D';
  HandheldProgramSteps: string[] = [];
  ProgramSteps: ThermalProgramStep[] = [];
  StepPointer: number = 0;
  Pointer: number = 0;
  Readings: thermalData[] = [];
  LoggedData: ThermalReadingData[] = [];
  averageData: ThermalReadingData;
  RollingData: ThermalReadingData[] = [];
  LastSegmentalPacket: ThermalReadingData[] = [];
  ManualSync: ThermalReadingData;
  CurrentReading: ThermalDataReading;
  ThermalSettings: ThermalSettings = new ThermalSettings();
  SpineLocation: SpinalLocation;
  Config: ThermalConfig;
  Status: ThermalStatus;
  Distance: number = 0;
  ScanState: ThermalScanStates = ThermalScanStates.Init;
  savedConfig: ThermalConfig;
  connecting: boolean = false;

  constructor(public ble: BLE, public events: Events, public logger: LogService) {
    super(ble, events, logger);

  }

  public async Connect(id: string) : Promise<void> {
    this.events.subscribe(ClaEvents.DEVICE_CONNECTED, state => {
      this.logger.info(`DEVICE_CONNECTED: ${state}`, [{'SourceContext': 'BleThermal'}]);
      if(!this.connecting) return;

      this.connected = state;
      this.connecting = false;
      this.events.publish(ClaEvents.THERM_CONNECTED, true);
      this.events.unsubscribe(ClaEvents.DEVICE_CONNECTED);
    });
    await this.SetupDevice(id);
    this.connecting = true;
  }

  public loadSettings(settings) {
    this.ThermalSettings = settings;
  }

  public async Disconnect() {
    //await this.StreamEnable(ThermalStreams.Main, false);
    await super.Disconnect();
    this.events.publish(ClaEvents.THERM_DISCONNECTED, true);
  }

  public async StartScanning(): Promise<boolean> {
    this.ResetData();
    this.SpineLocation = this.ThermalSettings.StartingLocation;
    this.ScanState = ThermalScanStates.Prescan;
    await this.StartStream();
    this.events.publish(ClaEvents.THERM_LISTENING, true);
    return true;
  }

  public async Start(): Promise<boolean> {
    this.ScanState = ThermalScanStates.Scanning;
    this.SpineLocation = this.ThermalSettings.StartingLocation;
    return true;
  }

  private async StartStream(): Promise<boolean> {
    let handheldProgram = this.GetProgram(this.HandheldProgramSteps, this.ThermalSettings.ScanMethod);
    await this.LoadProgram(handheldProgram);
    this.Pointer = 0;
    this.ThermalSettings.ScanMethod === ThermalScanMethod.Segmental && (this.Pointer = this.Pointer + 1)
    await this.ProgramStep(this.Pointer);
    await this.StreamEnable(ThermalStreams.Main, true);
    return true;
  }

  private GetProgram(steps: string[], scanType: ThermalScanMethod) : number[] {
    const encoder = new TextEncoder();
    const separator: string = String.fromCharCode(0);
    let filteredSteps = steps;
    for(let i = 0; i < filteredSteps.length; i++) {
      if(filteredSteps[i].length < 3) {
        filteredSteps[i] += String.fromCharCode(32);
      }
    }
    let method = (scanType === ThermalScanMethod.Rolling) ? 'ROL' : 'SEG';
    let filteredStepsString = method + String.fromCharCode(0) +  filteredSteps.join(separator) + `${separator}${separator}${separator}${separator}${separator}`;
    let byteSteps = encoder.encode(filteredStepsString);
    this.logger.warn(`GET PROGRAM INPUT ${JSON.stringify(steps)} OUTPUT ${Array.from(byteSteps)} ${filteredStepsString}`, [{'SourceContext': 'BleThermal'}]);
    return Array.from(byteSteps);
  }

  private ResetData() {
    this.StepPointer = 1;
    this.Readings = [];
    this.LoggedData = [];
    this.RollingData = [];
    this.averageData = undefined;
  }

  private async LoadProgram(program: number[]) {
    await this.SendCommand(CommandDefinitions.CmdWriteProgram, program, true);
  }

  public getPacket () {
    return this.Readings;
  }

  public getRollingPacket () {
    return this.encode(this.RollingData);
  }

  private encode(thermalData: ThermalReadingData[]) {
    this.logger.debug(`encoding data: ${thermalData.length}`, [{'SourceContext': 'BleThermal'}]);
    const dataViewBufferSize = thermalData.length * 24;

    const buffer = new ArrayBuffer(dataViewBufferSize);

    const view = new DataView(buffer);

    thermalData.forEach(({LeftTherm, RightTherm, LeftIr, RightIr, Distance, Location}, index) => {

      const offset = index * 24;
      view.setFloat32(offset, LeftTherm, true);
      view.setFloat32(offset + 4, RightTherm, true);
      view.setFloat32(offset + 8, LeftIr, true);
      view.setFloat32(offset + 12, RightIr, true);
      view.setFloat32(offset + 16, Distance, true);
      view.setInt32(offset + 20, Location, true);

    });
    const newData =  Array.from(new Uint8Array(view.buffer))
      .map(x => this.padStart(x.toString(16), 2, '0'))
      .join('');

      const c1LeftResult = thermalData.filter(r => r.Location === SpinalLocation.C1L)[0];
      const c1RightResult = thermalData.filter(r => r.Location === SpinalLocation.C1R)[0];

      let offset =  {
        left: 1,
        right: 1
      };
      offset.right = this.Config.IrCh1Sync;
      offset.left = this.Config.IrCh2Sync;
      let c1left = 0;
      let c1right = 0;
      if (c1LeftResult && c1RightResult) {
            c1left = c1LeftResult.LeftTemp;
            c1right = c1RightResult.RightTemp;
      } else {
        this.logger.warn(`Invalid C1 Data`, [{'SourceContext': 'BleThermal'}]);
      }
      // data we send back
    return {
        c1left,
        c1right,
        data: newData,
        offset
      };
  }

  public async redoRolling() {
    this.Pointer = this.Pointer - 2;
    await this.ProgramStep(this.Pointer, true);
    (this.ScanState === ThermalScanStates.ScanningRightC1) && (this.ScanState = ThermalScanStates.ScanningLeftC1);
    (this.ScanState === ThermalScanStates.AwaitingComplete) && (this.ScanState = ThermalScanStates.ScanningRightC1);
  }

  public async redo () {
    // fix when dual left
    const curStep = this.ProgramSteps[this.StepPointer - 1];
    if (curStep) {
      if(curStep.Dual && curStep.LeftComplete) {
        delete curStep.LeftTherm;
        delete curStep.LeftIr;
        return;
      }
    }

    this.StepPointer !== 1 && (this.StepPointer = this.StepPointer - 1);
    this.Pointer = this.Pointer - 2;
    await this.ProgramStep(this.StepPointer, true);
    // fix when dual right
    const curStepBlw = this.ProgramSteps[this.StepPointer - 1];
    if(curStepBlw.Dual && curStepBlw.LeftComplete) {
      delete curStepBlw.LeftTherm;
      delete curStepBlw.LeftIr;
    }

  }

  private async ProgramStep(position: number, step: boolean = true) {
    if(step) {
      await this.SendCommand(CommandDefinitions.CmdSetPc, [position], true);
      this.Pointer++;
    } else {
      await this.SendCommand(CommandDefinitions.CmdSetPc, [position - 1], true);
    }
  }

  private async CompleteScan() {
    await this.StreamEnable(ThermalStreams.Main, false);
  }

  async ProcessStatus(data: ArrayBuffer) {
    const view = new DataView(data);
    this.Status = {
      BatteryVoltage: view.getUint16(12, true),
      DcInputVoltage: view.getUint16(10, true),
      Firmware: "",
      FirmwareMinor: view.getUint8(6),
      FirmwareMajor: view.getUint8(7),
      Flag: view.getUint8(9),
      StreamStatus: view.getUint8(8),
      ProgramCounter: view.getUint8(14)
    };
    this.Status.Firmware = `${this.Status.FirmwareMajor}.${this.Status.FirmwareMinor}`;
    this.ReportPower(this.Status.BatteryVoltage, 4120, 3560);

    this.logger.debug(`found status ${JSON.stringify(this.Status)}`, [{'SourceContext': 'BleThermal'}]);
    // only used for testing
    this.events.publish(ClaEvents.THERM_STATUS_RX, this.Status);
  }

  async ProcessConfig(data: ArrayBuffer) {
    const view = new DataView(data);
    this.Config = {
      DisplayRange: view.getUint8(63),
      IrCh1Gain: view.getFloat32(39, true),
      IrCh1Offset: view.getFloat32(43, true),
      IrCh1Sync: view.getFloat32(55, true),
      IrCh2Gain: view.getFloat32(47, true),
      IrCh2Offset: view.getFloat32(51, true),
      IrCh2Sync: view.getFloat32(59, true),
      LcdXDimension: view.getUint8(19),
      LcdYDimension: view.getUint8(20),
      MyAddress: 1,
      SerialNumber: view.getUint32(7, true),
      ThermistorCh1Gain: view.getFloat32(23, true),
      ThermistorCh1Offset: view.getFloat32(27, true),
      ThermistorCh2Gain: view.getFloat32(31, true),
      ThermistorCh2Offset: view.getFloat32(35, true),
      TouchThreshold: view.getUint16(21, true),
      XMaxCounts: view.getUint16(13, true),
      XOffsetCounts: view.getUint16(11, true),
      YMaxCounts: view.getUint16(17, true),
      YOffsetCounts: view.getUint16(15, true)
    };
    this.logger.debug(`found config ${JSON.stringify(this.Config)}`, [{'SourceContext': 'BleThermal'}]);
    // only used for testing
    this.events.publish(ClaEvents.THERM_CONFIG_RX, this.Config);

  }

  async ProcessData(data: ArrayBuffer) {
    const view = new DataView(data);
    let rightTherm = view.getFloat32(5, true);
    let leftTherm = view.getFloat32(9, true);
    let rightIr = view.getFloat32(13, true);
    let leftIr = view.getFloat32(17, true);
    let encoder = view.getUint8(21);

    let reading: ThermalReadingData = new ThermalReadingData(encoder, leftIr, leftTherm, rightIr, rightTherm, Date.now());

    const speed = reading.Distance
    this.Distance += speed;
    this.LoggedData.push(reading);
    if(reading.Trigger) {
      await this.MarkReading(reading);
    }

    if (this.ThermalSettings.EnableCalibration) {
      this.calculateAve(reading);
      return;
    }
    const sendData: thermalData = {
      distanceReadings: this.Distance,
      eventMarkers: reading.Location ? reading.Location : 0,
      rightStable: reading.RightStable,
      leftStable: reading.LeftStable,
      leftReadings: reading.LeftTemp,
      rightReadings: reading.RightTemp,
      minDiffValue: reading.DiffTemp,
      leftIr: reading.LeftIr,
      rightIr: reading.RightIr
    }

    // only used for testing
    this.events.publish(ClaEvents.THERM_DATA_RX, sendData);

    if (this.ThermalSettings.ScanMethod === ThermalScanMethod.Rolling) {
      this.ScanState !== ThermalScanStates.Prescan && this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);
      this.ScanState === ThermalScanStates.Prescan && this.events.publish(ClaEvents.RAWDATA, sendData);
      this.events.publish(ClaEvents.SCANDATA_THERMAL_SPEED, this.ScanState === ThermalScanStates.Scanning ? speed : 0);
    } else {
      this.events.publish(ClaEvents.RAWDATA, sendData);
    }
    if(!reading.Trigger) {

    }

    if(this.ScanState == ThermalScanStates.Prescan || this.ScanState == ThermalScanStates.Init) return;
    await this.HandleRollingData(this.ThermalSettings, this.ScanState, reading);
  }

  public async MarkReading(readingData: ThermalReadingData) {
    this.logger.debug(`[MarkReading] ${this.StepPointer} reading: ${readingData} pointer: ${this.Pointer}`, [{'SourceContext': 'BleThermal'}]);
    if(this.ThermalSettings.EnableCalibration) {
      if(this.ManualSync.LeftTherm < 1) {
        if(this.averageData.LeftTemp < 80) {
          this.events.publish(ClaEvents.CALIBRATION_MESSAGE, 'Temperature too low, must be above 80°F');
          await this.ProgramStep(0);
          return;
        }
        this.ManualSync.LeftIr = this.averageData.LeftIr;
        this.ManualSync.LeftTherm = this.averageData.LeftTherm;
        const sendData = {
          leftTherm: this.averageData.LeftTherm,
          leftTemp: this.averageData.LeftTemp,
          rightTherm: 0,
          rightTemp: 0
        }
        this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);
        this.ResetData();
        await this.ProgramStep(0);
      } else {
        if(this.averageData.RightTemp < 80) {
          this.events.publish(ClaEvents.CALIBRATION_MESSAGE, 'Temperature too low, must be above 80°F');
          await this.ProgramStep(0);
          return;
        }
        this.ManualSync.RightIr = this.averageData.RightIr;
        this.ManualSync.RightTherm = this.averageData.RightTherm;
        const sendData = {
          leftTherm: 0,
          leftTemp: 0,
          rightTherm: this.averageData.RightTherm,
          rightTemp: this.averageData.RightTemp
        }
        this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);
        await this.ProgramStep(0);
        await this.CalibrateComplete(this.ManualSync);
      }
    } else if (this.ThermalSettings.ScanMethod == ThermalScanMethod.Segmental) {
      let curStep = this.ProgramSteps[0];
      if(this.StepPointer - 1 >= this.ProgramSteps.length) {
        curStep = this.ProgramSteps[this.ProgramSteps.length -1];
        await this.SendCommand(CommandDefinitions.CmdSetPc, [this.StepPointer]);
      } else {
        curStep = this.ProgramSteps[this.StepPointer - 1];
        await this.SendCommand(CommandDefinitions.CmdSetPc, [this.StepPointer]);
      }
      let proceed = false;
      if(this.ScanState == ThermalScanStates.Scanning) {
        readingData.Location = curStep.Location;
        // this.Readings.push(readingData);
      } else if (this.ScanState == ThermalScanStates.Prescan) {
        readingData.Location = curStep.Location;
        this.ResetData();
        await this.Start();
      }
      if(curStep.Dual) {
        this.logger.debug(`[MarkReading] Dual Step ${this.StepPointer} reading: ${readingData} pointer: ${this.Pointer}`, [{'SourceContext': 'BleThermal'}]);
        if(!curStep.LeftComplete) {
          curStep.LeftIr = readingData.LeftIr;
          curStep.LeftTherm = readingData.LeftTherm;

          const sendData: thermalData = {
            eventMarkers: readingData.Location,
            rightStable: readingData.RightStable,
            leftStable: readingData.LeftStable,
            leftReadings: readingData.LeftTemp,
            rightReadings: 0,
            minDiffValue: readingData.DiffTemp,
            leftIr: readingData.LeftIr,
            rightIr: readingData.RightIr
          }
          this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);

          return; // Go no further, we need to get right reading before we move on
        } else {
          readingData.LeftIr = curStep.LeftIr;
          readingData.LeftTherm = curStep.LeftTherm;
          proceed = true;
        }
      } else {
        proceed = true;
      }

      if(this.StepPointer - 1  >= this.ProgramSteps.length) {
        this.logger.debug(`[MarkReading] StepPointer ${this.StepPointer} out of range of ProgramSteps ${this.ProgramSteps}`, [{'SourceContext': 'BleThermal'}]);
        await this.CompleteScan();
        return;
      }
      const sendData: thermalData = {
        eventMarkers: readingData.Location,
        rightStable: readingData.RightStable,
        leftStable: readingData.LeftStable,
        leftReadings: readingData.LeftTemp,
        rightReadings: readingData.RightTemp,
        minDiffValue: readingData.DiffTemp,
        leftIr: readingData.LeftIr,
        rightIr: readingData.RightIr
      }

      this.Readings.push(sendData);
      this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);
      this.StepPointer++;
      await this.ProgramStep(this.StepPointer, proceed);
      this.logger.debug(`[MarkReading] StepPointer ${this.StepPointer} reading: ${readingData} pointer: ${this.Pointer}`, [{'SourceContext': 'BleThermal'}]);
    } else {
      // Rolling
      if(this.ScanState == ThermalScanStates.ScanningRightC1) {
        readingData.Location = SpinalLocation.C1R;

        this.ScanState = ThermalScanStates.AwaitingComplete;
      } else if (this.ScanState == ThermalScanStates.ScanningLeftC1) {
        readingData.Location = SpinalLocation.C1L;

        this.ScanState = ThermalScanStates.ScanningRightC1;
      } else if (this.ScanState == ThermalScanStates.Scanning) {
        if(this.ThermalSettings.SkipCheckpoints || this.SpineLocation <= SpinalLocation.T1)
        {
          this.SpineLocation = SpinalLocation.C2;
          readingData.Location = SpinalLocation.C2;
          this.ScanState = ThermalScanStates.ScanningLeftC1;
        } else if (this.SpineLocation <= SpinalLocation.L1) {
          this.SpineLocation = SpinalLocation.T1;
          readingData.Location = SpinalLocation.T1;
        } else if (this.SpineLocation <= SpinalLocation.S1) {
          this.SpineLocation = SpinalLocation.L1;
          readingData.Location = SpinalLocation.L1;
        }

      } else {
        this.ResetData();
        readingData.Location = this.ThermalSettings.StartingLocation;
        this.ScanState = ThermalScanStates.Scanning;
        await this.ProgramStep(this.Pointer, true);
        await this.Start();
        return;
      }
     await this.ProgramStep(this.Pointer, true);
    }
  }

  public async HandleRollingData(settings: ThermalSettings, state: ThermalScanStates, data: ThermalReadingData) {
    if(settings.ScanMethod != ThermalScanMethod.Rolling) return;

    let transportData = {
      LeftReadings: data.LeftTemp,
      RightReadings: data.RightTemp,
      DistanceReadings: this.Distance,
      EventMarkers: data.Location,
      LeftStable: data.LeftStable,
      RightStable: data.RightStable
    }

    const sendData: thermalData = {
      distanceReadings: transportData.DistanceReadings,
      eventMarkers: data.Location ? data.Location : 0,
      rightStable: transportData.RightStable,
      leftStable: transportData.LeftStable,
      leftReadings: transportData.LeftReadings,
      rightReadings: transportData.RightReadings,
      minDiffValue: data.DiffTemp,
      leftIr: data.LeftIr,
      rightIr: data.RightIr
    }
    this.events.publish(ClaEvents.RAWDATA, sendData);
    // prevent data before the first trigger
    this.ScanState !== ThermalScanStates.Prescan && this.Readings.push(sendData);

    if(this.ScanState == ThermalScanStates.Complete) {
      transportData.EventMarkers = -1;
    } else if(this.ScanState == ThermalScanStates.Scanning ||
      data.Location == SpinalLocation.C2 ||
      data.Location == SpinalLocation.C1L ||
      data.Location == SpinalLocation.C1R) {
      this.RollingData.push(data);
    }
  }

  public async CalibrateStart() {
    this.ResetData();
    this.ThermalSettings.EnableCalibration = true;
    this.ManualSync = new ThermalReadingData(0,0,0,0,0,Date.now());
    await this.LoadProgramByString("AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQ==");
    await this.StartScanning();
  }
  public async CancelCalibrate() {
    if(!this.connected) return;
    this.Config.IrCh1Sync = this.savedConfig.IrCh1Sync;
    this.Config.IrCh2Sync = this.savedConfig.IrCh2Sync;
    await this.WriteConfig();
    await this.Disconnect();
  }

  public async CalibrateReset(noDisconnect?: boolean) {
    if (noDisconnect) {
      this.savedConfig = { ...this.Config};
    }
    this.Config.IrCh1Sync = 1;
    this.Config.IrCh2Sync = 1;
    await this.WriteConfig();
    !noDisconnect && await this.Disconnect();
    this.logger.debug(`Config compare ${JSON.stringify(this.Config)} ${JSON.stringify(this.savedConfig)}`, [{'SourceContext': 'BleThermal'}]);
  }

  private calculateAve(reading: ThermalReadingData) {
    if(!this.averageData) {
      this.averageData = reading;
    }
    if (this.ManualSync.LeftTherm < 1) {
      reading.LeftIr = (this.averageData.LeftIr + reading.LeftIr)/ 2;
      reading.LeftTherm = (this.averageData.LeftTherm + reading.LeftTherm) / 2;
      this.averageData.LeftIr = reading.LeftIr;
      this.averageData.LeftTherm = reading.LeftTherm;
    } else {
      this.averageData.RightIr = reading.RightIr;
      this.averageData.RightTherm = reading.RightTherm;
      reading.RightIr = (this.averageData.RightIr + reading.RightIr) / 2;
      reading.RightTherm = (this.averageData.RightTherm + reading.RightTherm) / 2;
    }
    const sendData: thermalData = {
      distanceReadings: this.Distance,
      eventMarkers: reading.Location ? reading.Location : 0,
      rightStable: reading.RightStable,
      leftStable: reading.LeftStable,
      leftReadings: reading.LeftTemp,
      rightReadings: reading.RightTemp,
      minDiffValue: reading.DiffTemp,
      leftIr: reading.LeftIr,
      rightIr: reading.RightIr
    }
    this.events.publish(ClaEvents.RAWDATA, sendData);

  }

  public async CalibrationSave(data: ThermalReadingData[], useLeft: boolean): Promise<ThermalReadingData> {
    this.logger.debug(`all data ${JSON.stringify(data)}`, [{'SourceContext': 'BleThermal'}]);
    let startTime = Date.now();
    let reading = new ThermalReadingData(0,0,0,0,0,Date.now());

    //remove last 2 readings
    data.splice(data.length - 3, 2);
    let filteredData = data.filter(d => d.Timestamp < startTime);
    if(useLeft) {
      let leftIrs = filteredData.map(d => d.LeftIr);
      let leftTherms = filteredData.map(d => d.LeftTherm);
      reading.LeftIr = leftIrs.reduce((a, b) => a + b) / leftIrs.length;
      reading.LeftTherm = leftTherms.reduce((a, b) => a + b) / leftTherms.length;
      this.logger.debug(`[CalibrateLeft] Reading: ${JSON.stringify(reading)}`, [{'SourceContext': 'BleThermal'}]);
    } else {
      let rightIrs = filteredData.map(d => d.RightIr);
      let rightTherms = filteredData.map(d => d.RightTherm);
      reading.RightIr = rightIrs.reduce((a, b) => a + b) / rightIrs.length;
      reading.RightTherm = rightTherms.reduce((a, b) => a + b) / rightTherms.length;
      this.logger.debug(`[CalibrateRight] Reading: ${JSON.stringify(reading)}`, [{'SourceContext': 'BleThermal'}]);
    }

    if(reading.LeftTemp + reading.RightTemp < 80) {
      this.events.publish(ClaEvents.CALIBRATION_MESSAGE, 'Temperature too low, must be above 80°F');
      return new ThermalReadingData(0,0,0,0,0,Date.now());
    }
    const sendData = {
      leftTherm: reading.LeftTherm,
      leftTemp: reading.LeftTemp,
      rightTherm: reading.RightTherm,
      rightTemp: reading.RightTemp
    }

    this.events.publish(ClaEvents.SCANDATA_THERMAL_RX, sendData);
    return reading;
  }

  public async CalibrateComplete(data: ThermalReadingData) {
    const ACCEPTVARIANCE = 0.3;
    this.ThermalSettings.EnableCalibration = false;
    let results = {
      data: {
        rightTemp: data.RightTemp,
        leftTemp: data.LeftTemp,
        diff: data.DiffTemp,
      },
      left: 1,
      right: 1,
      match: false,
    }
    if(data.DiffTemp > ACCEPTVARIANCE) {
      let sync = data.LeftTemp > data.RightTemp ? (data.RightIr + data.DiffTemp) / data.RightIr : (data.LeftIr + data.DiffTemp) / data.LeftIr;

      sync = Math.abs(sync);
      if(data.RightTemp > data.LeftTemp) {
        this.Config.IrCh1Sync = 1;
        this.Config.IrCh2Sync = sync;
        results.left = sync;
        results.right = 1;
      } else {
        this.Config.IrCh1Sync = sync;
        this.Config.IrCh2Sync = 1;
        results.left = 1;
        results.right = sync;
      }

      if(this.savedConfig.IrCh2Sync + 0.3 >= results.left && this.savedConfig.IrCh2Sync - 0.3 <= results.left && this.savedConfig.IrCh1Sync + 0.3 >= results.right && this.savedConfig.IrCh1Sync - 0.3 <= results.right) {
        results.match = true;
      }
      await this.WriteConfig();
    }
    this.events.publish(ClaEvents.CALIBRATIONCOMPLETE_RESULT, results);

    await this.CompleteScan();
    await this.Disconnect();

  }

  public async WriteConfig() {
    let extras = [];
    extras.push(this.Config.MyAddress);
    extras.push(...this.getBytes(this.Config.SerialNumber, 4, 1));
    extras.push(...this.getBytes(this.Config.XOffsetCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.XMaxCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.YOffsetCounts, 2, 3));
    extras.push(...this.getBytes(this.Config.YMaxCounts, 2, 3));
    extras.push(this.Config.LcdXDimension);
    extras.push(this.Config.LcdYDimension);
    extras.push(...this.getBytes(this.Config.TouchThreshold, 2, 3));
    extras.push(...this.getBytes(this.Config.ThermistorCh1Gain, 4, 2));
    extras.push(...this.getBytes(this.Config.ThermistorCh1Offset, 4, 2));
    extras.push(...this.getBytes(this.Config.ThermistorCh2Gain, 4, 2));
    extras.push(...this.getBytes(this.Config.ThermistorCh2Offset, 4,2));
    extras.push(...this.getBytes(this.Config.IrCh1Gain, 4, 2));
    extras.push(...this.getBytes(this.Config.IrCh1Offset, 4, 2));
    extras.push(...this.getBytes(this.Config.IrCh2Gain, 4, 2));
    extras.push(...this.getBytes(this.Config.IrCh2Offset, 4, 2));
    extras.push(...this.getBytes(this.Config.IrCh1Sync, 4, 2));
    extras.push(...this.getBytes(this.Config.IrCh2Sync, 4, 2));
    extras.push(this.Config.DisplayRange);

    this.logger.info(`Will write config ${JSON.stringify(extras)}`, [{'SourceContext': 'BleThermal'}]);
   await this.SendCommand(CommandDefinitions.CmdWriteConfig, extras);
  }

  private getBytes(value: number, length: number, type: number) {
    let b = new ArrayBuffer(length);
    switch (type) {
      case 1: {
        new DataView(b).setUint32(0, value, true);
        break;
      }
      case 2: {
        new DataView(b).setFloat32(0, value, true);
        break;
      }
      case 3: {
        new DataView(b).setUint16(0, value, true);
        break;
      }
    }
    return Array.from(new Uint8Array(b));
  }

  public async LoadProgramByString(encodedString: string) {
    const encoder = new TextEncoder();
    let data: Uint8Array = encoder.encode(encodedString);
    let steps: number[] = Array.from(data);
    this.HandheldProgramSteps = [];
    this.ProgramSteps = [];
    for(let i = steps.length - 1; i >= 0; i--) {
      if(steps[i] === 0) continue;

      let location: ThermalProgramStep = new ThermalProgramStep()
      location.Location = SpinalLocation[SpinalLocation[i]];
      location.Dual = steps[i] === 2;
      this.HandheldProgramSteps.push(SpinalLocation[location.Location]);
      this.ProgramSteps.push(location);
    }
    (this.ThermalSettings.ScanMethod === ThermalScanMethod.Rolling) && this.loadRolling();
    this.GetProgram(this.HandheldProgramSteps, this.ThermalSettings.ScanMethod);
  }

  private loadRolling() {
    // clear existing location for rolling only
    this.HandheldProgramSteps = [];
    this.ProgramSteps = [];
    // add stating location
    let location: ThermalProgramStep = new ThermalProgramStep()
    location.Location = SpinalLocation[SpinalLocation[this.ThermalSettings.StartingLocation]];
    location.Dual = false;
    this.HandheldProgramSteps.push(SpinalLocation[location.Location]);
    this.ProgramSteps.push(location);
    if (!this.ThermalSettings.SkipCheckpoints) {
      this.logger.debug(`adding rolling thermal protocol`, [{'SourceContext': 'BleThermal'}]);
      let l1 = new ThermalProgramStep();
      l1.Location = SpinalLocation.L1;
      l1.Dual = false;
      if (l1.Location < location.Location) {
        this.HandheldProgramSteps.push(SpinalLocation[l1.Location]);
        this.ProgramSteps.push(l1);
      }

      let t1 = new ThermalProgramStep();
      t1.Location = SpinalLocation.T1;
      t1.Dual = false;
      if (t1.Location < location.Location) {
        this.HandheldProgramSteps.push(SpinalLocation[t1.Location]);
        this.ProgramSteps.push(t1);
      }
    }
    let c2 = new ThermalProgramStep();
    c2.Location = SpinalLocation.C2;
    c2.Dual = false;
    this.HandheldProgramSteps.push(SpinalLocation[c2.Location]);
    this.ProgramSteps.push(c2);

    let c1 = new ThermalProgramStep();
    c1.Location = SpinalLocation.C1;
    c1.Dual = false;
    this.HandheldProgramSteps.push(SpinalLocation[c1.Location]);
    this.ProgramSteps.push(c1);
  }
}

export enum ThermalStreams
{
  Main = 1
}



// WEBPACK FOOTER //
// ./src/instruments/BleThermal.ts